import { css } from "@emotion/css";
import type { TextProps } from "@octopusdeploy/design-system-components";
import { Note, DeprecatedIconButton } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { ParseHelper } from "../../../utils";
import type { FormFieldProps } from "../FormFieldProps";
import { TextWithLabel } from "../Text/TextWithLabel";
type NumberOtherOmitProps = Omit<TextProps, keyof FormFieldProps<string> | "type">;
interface NumberOtherProps {
    note?: string;
    label?: string;
    unitOfMeasure?: string;
}
type NumberProps = NumberOtherOmitProps & FormFieldProps<number | undefined> & NumberOtherProps;
/* @deprecated
 * Deprecated: 28/11/2025
 * Migration guidelines: This component is deprecated and will be removed in a future release.
 * This is part of our ongoing improvements to forms and input components to enhance usability and accessibility.
 *
 * Please migrate to the NumberField component where possible.
 *
 * We recognize NumberField has different styling; if that prevents an immediate swap, you may continue using this
 * component until all the new form components are in place.
 */
export function DeprecatedNumberPicker(props: NumberProps) {
    const { value, onChange, label, note, unitOfMeasure, ...otherProps } = props;
    const handleChange = (newValue: string) => {
        const newValueParsed = newValue ? ParseHelper.safeParseInt(newValue, 0) : undefined;
        if (onChange)
            onChange(newValueParsed);
    };
    const increment = () => {
        if (value === undefined)
            handleChange("1");
        else
            handleChange((value + 1).toString());
    };
    const decrement = () => {
        if (value === undefined)
            handleChange("0");
        else
            handleChange((value - 1).toString());
    };
    return (<>
            <label className={labelStyles}>{label}</label>
            <div className={inputContainerStyles}>
                <div className={numberPickerStyles}>
                    <div className={digitStyles}>
                        <DeprecatedIconButton accessibleName={`Increment ${unitOfMeasure ?? "value"}`} icon="CaretUpIcon" onClick={() => increment()} tabIndex={-1}/>
                        <TextWithLabel type="number" onChange={handleChange} onKeyPress={(e) => {
            if (e.key === ".") {
                e.preventDefault();
            }
        }} value={value === undefined ? "" : value.toString()}/>
                        <DeprecatedIconButton accessibleName={`Decrement ${unitOfMeasure ?? "value"}`} icon="CaretDownIcon" onClick={() => decrement()} tabIndex={-1}/>
                    </div>
                </div>
                {!!unitOfMeasure && <span className={unitOfMeasureStyles}>{unitOfMeasure}</span>}
            </div>
            <Note>{note}</Note>
        </>);
}
export default DeprecatedNumberPicker;
const labelStyles = css({
    display: "block",
    marginTop: space[16],
    font: text.body.regular.medium,
    color: themeTokens.color.text.primary,
});
const inputContainerStyles = css({
    display: "flex",
    flexDirection: "row",
});
const numberPickerStyles = css({
    display: "flex",
    alignItems: "center",
    marginTop: space[16],
});
const digitStyles = css({
    width: "4rem",
    marginRight: space[4],
    "& button": {
        margin: "auto",
    },
    "&:first-of-type": {
        marginRight: space[16],
    },
    // remove spinners
    "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button": {
        WebkitAppearance: "none",
        margin: 0,
    },
    "& input[type=number]": {
        MozAppearance: "textfield",
    },
    "& input": {
        textAlign: "center",
    },
});
const unitOfMeasureStyles = css({
    marginTop: space[16],
    display: "inline-block",
    alignSelf: "center",
    font: text.body.regular.medium,
    color: themeTokens.color.text.primary,
});
